import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Animated, View, StyleSheet, Easing } from 'react-native';
import { BMPrimitives } from '../../theme';
const ANIMATION_START_DELAY = BMPrimitives.BMAnimation.duration.instant;
const SCALE_MIN = 1 / 3;
const SCALE_MAX = 1;
const OPACITY_MIN = BMPrimitives.BMOpacity.washed;
const OPACITY_MAX = 1;
const DOT_SIZE = 12;
const DOT_COUNT = 3;
const animationConfig = {
    duration: BMPrimitives.BMAnimation.duration.quick,
    easing: Easing.inOut(Easing.ease),
};
function createAnimations(count) {
    // We can't use fill directly with new Animated.Value because entire array values
    // will references the same instance. That's why we fill it first with empty string
    // to remove hole in array, and map over them to Animated.Value
    return new Array(count).fill('').map(() => new Animated.Value(SCALE_MIN));
}
function terminateAnimations(animations) {
    // Stopping animation is not enough because Animated.View still
    // references old Animated.Value, that's why we mutate them manually
    // to initial Animated.Value
    animations.forEach((v) => v.setValue(SCALE_MIN));
}
const BMDotLoader = forwardRef((props, ref) => {
    const { autoplay = true, style, color = BMPrimitives.BMBrand.primary, ...restProps } = props;
    const animations = useRef(createAnimations(DOT_COUNT)).current;
    let animation = useRef(null).current;
    function createAnimationLoop() {
        animation = Animated.loop(Animated.stagger(ANIMATION_START_DELAY, animations.map((value) => {
            return Animated.sequence([
                Animated.timing(value, {
                    toValue: SCALE_MAX,
                    useNativeDriver: true,
                    ...animationConfig,
                }),
                Animated.timing(value, {
                    toValue: SCALE_MIN,
                    useNativeDriver: true,
                    ...animationConfig,
                }),
            ]);
        })));
    }
    useEffect(() => {
        createAnimationLoop();
        if (autoplay)
            animation === null || animation === void 0 ? void 0 : animation.start();
    }, []);
    useImperativeHandle(ref, () => ({
        start: () => {
            // We need to recreate animation loops because restarting stopped animation
            // is not possible with just animation.start() call
            createAnimationLoop();
            // Because we have new instance of animation sequence, we can run .start
            // to trigger both start and restart event
            animation === null || animation === void 0 ? void 0 : animation.start();
        },
        stop: () => {
            animation === null || animation === void 0 ? void 0 : animation.stop();
            terminateAnimations(animations);
        },
    }), []);
    return (<View {...restProps} style={[styles.container, style]}>
      {animations.map((value, index) => (<Animated.View key={index} style={[
                styles.dot,
                {
                    backgroundColor: color,
                    transform: [{ scale: value }],
                    opacity: value.interpolate({
                        inputRange: [SCALE_MIN, SCALE_MAX],
                        outputRange: [OPACITY_MIN, OPACITY_MAX],
                    }),
                },
            ]}/>))}
    </View>);
});
export default BMDotLoader;
const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
    dot: {
        height: DOT_SIZE,
        width: DOT_SIZE,
        borderRadius: DOT_SIZE / 2,
        marginRight: 2,
    },
});
